import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, track } from "@/lib/api";
import { waLink } from "@/constants/site";

const UNIT_LABEL = { "sq ft": "per sq ft", seat: "per seat", panel: "per panel", item: "per item", project: "" };

export default function Pricing() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    track("page_view", { page: "pricing" });
    api.get("/catalogue").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const groups = ["Film", "Coating", "Specialty"].map((t) => ({
    type: t, items: services.filter((s) => s.protection_type === t),
  })).filter((g) => g.items.length);

  const priceText = (s) => {
    if (s.pricing_unit === "project" || !s.base_rate) return "Custom quote";
    return `₹${Number(s.base_rate).toLocaleString("en-IN")}`;
  };

  return (
    <div className="bg-[#080808]" data-testid="pricing-page">
      <Seo title="Pricing" description="Transparent starting prices for ERRAH surface protection — coatings, SPF film and specialty systems. Get an exact quote with the AI Surface Scan." />

      <section className="max-w-[1200px] mx-auto px-5 lg:px-10 pt-28 pb-8">
        <Reveal>
          <p className="errah-label gold-text mb-4">Pricing</p>
          <h1 className="serif-head text-4xl sm:text-5xl lg:text-7xl max-w-3xl">Transparent starting prices.</h1>
          <p className="mt-6 text-gray-400 max-w-2xl leading-relaxed">
            Every ERRAH service is priced from the surface up. The figures below are starting points — final pricing may vary based on surface condition, dimensions, preparation and installation requirements.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 lg:px-10 pb-16 space-y-12">
        {groups.map((g, gi) => (
          <Reveal key={g.type} delay={gi * 0.05}>
            <p className="errah-label gold-text mb-4">{g.type === "Film" ? "SPF — Surface Protection Film" : g.type === "Coating" ? "Surface Protection Coatings" : "Specialty Protection"}</p>
            <div className="hair-border divide-y divide-white/10" data-testid={`pricing-group-${g.type}`}>
              {g.items.map((s) => (
                <div key={s.id} className="p-5 lg:p-6 grid md:grid-cols-[1fr_auto] gap-4 items-center bg-white/[0.02]">
                  <div>
                    <h3 className="font-display text-lg">{s.service_name}</h3>
                    <p className="mt-1.5 text-sm text-gray-400 leading-relaxed max-w-2xl">{s.description}</p>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                      {s.warranty && <span>{s.warranty} warranty</span>}
                      {s.site_inspection_required && <span className="text-[#E4C987]">Site inspection recommended</span>}
                    </div>
                  </div>
                  <div className="md:text-right">
                    <p className="font-display text-2xl gold-text whitespace-nowrap">{priceText(s)}</p>
                    <p className="text-[11px] text-gray-500">{s.pricing_unit === "project" ? "after assessment" : `${UNIT_LABEL[s.pricing_unit] || "per unit"} · min ₹${Number(s.min_charge || 0).toLocaleString("en-IN")}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-t border-white/10 bg-warmblack">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="serif-head text-2xl lg:text-4xl">Want an exact price?</h2>
            <p className="mt-2 text-gray-400 max-w-xl">Scan your surface and ERRAH AI gives you an estimated investment in seconds — or request a detailed quote.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/ai-scan")} data-testid="pricing-scan-cta"
              className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
              <ScanLine size={16} /> Scan Your Surface
            </button>
            <a href={waLink()} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
